import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Sparkles } from "lucide-react";
import { FeaturedCarousel } from "@/components/create/explore/FeaturedCarousel";
import { FloatingSearch } from "@/components/create/explore/FloatingSearch";
import { CategoryChips, PresetGrid } from "@/components/create/explore/PresetGrid";
import { RecentStrip } from "@/components/create/explore/RecentStrip";
import { ShortcutTiles } from "@/components/create/explore/ShortcutTiles";
import { StudioHeader } from "@/components/create/explore/StudioHeader";
import { EASE, hfOpen, imageOpen, useCalmMotion, useGenerations, useMediaConfig, useOpenPreset, usePresets, videoOpen } from "@/lib/studio";

function Reveal({ i, children }) {
  const calm = useCalmMotion();
  return (
    <motion.div initial={calm ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.08 * i }}>
      {children}
    </motion.div>
  );
}

function PresetsSection({ presets, categories, onOpen }) {
  const [category, setCategory] = useState("All");
  const shown = presets.filter((p) => category === "All" || p.category === category).slice(0, 12);
  return (
    <section className="space-y-4" data-testid="explore-presets">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-300">Recreate with one tap <Sparkles className="h-4 w-4" /></p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">Presets</h2>
        </div>
        <Link to="/create/presets" className="flex items-center gap-0.5 pb-1 text-sm font-medium text-zinc-400 transition-colors hover:text-white" data-testid="preset-view-all-link">
          View all <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <CategoryChips categories={categories} value={category} onChange={setCategory} />
      {shown.length ? <PresetGrid presets={shown} onOpen={onOpen} /> : <p className="py-10 text-center text-sm text-zinc-500" data-testid="explore-presets-empty">No presets in this category yet.</p>}
    </section>
  );
}

export default function ExplorePage() {
  const config = useMediaConfig().data;
  const presetsQ = usePresets();
  const gens = useGenerations().data?.items || [];
  const onOpen = useOpenPreset();
  const presets = useMemo(() => (presetsQ.data?.items || []).filter((p) => p.status === "published"), [presetsQ.data]);
  const featured = presets.filter((p) => p.featured).slice(0, 6);
  const firstOf = (fn) => presets.find(fn)?.cover_url;
  const latest = gens.find((g) => g.status === "complete" && g.thumb_url);
  return (
    <div className="h-full overflow-y-auto" data-testid="create-page">
      <StudioHeader title="Explore" />
      <div className="mx-auto w-full max-w-6xl space-y-9 px-4 pb-32 pt-4 sm:px-6 sm:pt-6 lg:pb-16">
        <Reveal i={0}><FeaturedCarousel presets={featured.length ? featured : presets.slice(0, 4)} onOpen={onOpen} /></Reveal>
        <Reveal i={1}>
          <ShortcutTiles imageLocked={config ? !imageOpen(config) && !hfOpen(config) : false} videoLocked={config ? !videoOpen(config) && !hfOpen(config) : false}
            proLocked={config ? !hfOpen(config) : false} influencerCover={firstOf((p) => p.category === "Portraits")} adsCover={firstOf((p) => p.slug === "marble-studio-swap")} assetsCount={gens.length}
            imageCover={firstOf((p) => p.kind === "image" && p.operation === "create" && p.category !== "Portraits")} videoCover={firstOf((p) => p.kind === "video")}
            assetsCover={latest?.thumb_url || firstOf((p) => p.operation === "edit")} />
        </Reveal>
        <Reveal i={2}><RecentStrip items={gens} /></Reveal>
        <Reveal i={3}><PresetsSection presets={presets} categories={presetsQ.data?.categories || []} onOpen={onOpen} /></Reveal>
      </div>
      <FloatingSearch />
    </div>
  );
}
